import React from 'react';
import styled, { css } from 'styled-components';

export type StudioLayoutProps = {
  rail?: React.ReactNode;
  inspector?: React.ReactNode;
  topbar?: React.ReactNode;
  beatStrip?: React.ReactNode;
  railWidth?: number;
  inspectorWidth?: number;
  /**
   * Vertical pixels reserved by a parent (e.g. app header) above this layout.
   * Used to compute viewport-aware heights so the studio fits inside the
   * remaining vertical space instead of overflowing.
   */
  topOffsetPx?: number;
  defaultRailOpen?: boolean;
  defaultInspectorOpen?: boolean;
  children: React.ReactNode;
  className?: string;
};

const RAIL_BREAKPOINT = 1024;
const INSPECTOR_BREAKPOINT = 1200;

type ShellProps = {
  $railWidth: number;
  $inspectorWidth: number;
  $railOpen: boolean;
  $inspectorOpen: boolean;
  $topOffset: number;
};

const Shell = styled.div<ShellProps>`
  --studio-viewport-height: ${(p) =>
    p.$topOffset > 0 ? `calc(100vh - ${p.$topOffset}px)` : '100vh'};
  display: grid;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: var(--studio-viewport-height);
  background: var(--color-bg);
  overflow-x: hidden;
  grid-template-columns:
    ${(p) => (p.$railOpen ? `${p.$railWidth}px` : '0')}
    minmax(0, 1fr)
    ${(p) => (p.$inspectorOpen ? `${p.$inspectorWidth}px` : '0')};
  grid-template-rows: auto minmax(0, 1fr);
  grid-template-areas:
    'rail topbar inspector'
    'rail main inspector';
  transition: grid-template-columns var(--duration-base) var(--ease-emphasized);

  @media (max-width: ${INSPECTOR_BREAKPOINT}px) {
    grid-template-columns: ${(p) => (p.$railOpen ? `${p.$railWidth}px` : '0')} minmax(0, 1fr);
    grid-template-areas:
      'rail topbar'
      'rail main';
  }

  @media (max-width: ${RAIL_BREAKPOINT}px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'topbar'
      'main';
  }
`;

const Rail = styled.div<{ $open: boolean }>`
  grid-area: rail;
  position: sticky;
  top: 0;
  height: var(--studio-viewport-height);
  overflow: hidden;
  ${(p) =>
    !p.$open &&
    css`
      display: none;
    `};

  @media (max-width: ${RAIL_BREAKPOINT}px) {
    display: none;
  }
`;

const TopBar = styled.div`
  grid-area: topbar;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-5);
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border-subtle);
  position: sticky;
  top: 0;
  z-index: 2;
  min-width: 0;
`;

const Main = styled.main`
  grid-area: main;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-5) var(--space-6) var(--space-8);
  min-width: 0;
  min-height: 0;
  max-height: var(--studio-viewport-height);
  overflow-y: auto;
`;

const CanvasArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const BeatArea = styled.div`
  width: 100%;
  max-width: 1080px;
`;

const Inspector = styled.div<{ $open: boolean }>`
  grid-area: inspector;
  position: sticky;
  top: 0;
  height: var(--studio-viewport-height);
  overflow: hidden;
  ${(p) =>
    !p.$open &&
    css`
      display: none;
    `};

  @media (max-width: ${INSPECTOR_BREAKPOINT}px) {
    display: none;
  }
`;

const DrawerBackdrop = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  opacity: ${(p) => (p.$open ? 1 : 0)};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  transition: opacity var(--duration-base) var(--ease-standard);
  z-index: 50;
`;

const DrawerPanel = styled.div<{ $open: boolean; $side: 'left' | 'right'; $width: number }>`
  position: fixed;
  top: 0;
  bottom: 0;
  ${(p) => (p.$side === 'left' ? 'left: 0;' : 'right: 0;')}
  width: min(${(p) => p.$width}px, 92vw);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
  transform: translateX(${(p) => (p.$open ? '0' : p.$side === 'left' ? '-100%' : '100%')});
  transition: transform var(--duration-base) var(--ease-emphasized);
  z-index: 51;
  display: flex;
  flex-direction: column;
`;

type ToggleButtonProps = { $hidden?: boolean };

const ToggleButton = styled.button<ToggleButtonProps>`
  appearance: none;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  border-radius: var(--radius-md);
  height: 32px;
  padding: 0 var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${(p) => p.$hidden && 'display: none;'};

  &:hover {
    background: var(--color-surface-subtle);
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = () => setMatches(mql.matches);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
};

export const StudioLayout: React.FC<StudioLayoutProps> = ({
  rail,
  inspector,
  topbar,
  beatStrip,
  railWidth = 280,
  inspectorWidth = 340,
  topOffsetPx = 0,
  defaultRailOpen = true,
  defaultInspectorOpen = true,
  children,
  className,
}) => {
  const railCollapsedByViewport = useMediaQuery(`(max-width: ${RAIL_BREAKPOINT}px)`);
  const inspectorCollapsedByViewport = useMediaQuery(
    `(max-width: ${INSPECTOR_BREAKPOINT}px)`
  );

  const [railOpen, setRailOpen] = React.useState(defaultRailOpen);
  const [inspectorOpen, setInspectorOpen] = React.useState(defaultInspectorOpen);
  const [railDrawerOpen, setRailDrawerOpen] = React.useState(false);
  const [inspectorDrawerOpen, setInspectorDrawerOpen] = React.useState(false);

  return (
    <Shell
      className={className}
      $railWidth={railWidth}
      $inspectorWidth={inspectorWidth}
      $railOpen={railOpen && !railCollapsedByViewport}
      $inspectorOpen={inspectorOpen && !inspectorCollapsedByViewport}
      $topOffset={topOffsetPx}
    >
      {rail && <Rail $open={railOpen && !railCollapsedByViewport}>{rail}</Rail>}

      <TopBar>
        {rail && (
          <ToggleButton
            type="button"
            onClick={() => {
              if (railCollapsedByViewport) setRailDrawerOpen(true);
              else setRailOpen((v) => !v);
            }}
          >
            ☰ History
          </ToggleButton>
        )}
        {topbar}
        <Spacer />
        {inspector && (
          <ToggleButton
            type="button"
            onClick={() => {
              if (inspectorCollapsedByViewport) setInspectorDrawerOpen(true);
              else setInspectorOpen((v) => !v);
            }}
          >
            ✎ Prompt
          </ToggleButton>
        )}
      </TopBar>

      <Main>
        <CanvasArea>{children}</CanvasArea>
        {beatStrip && <BeatArea>{beatStrip}</BeatArea>}
      </Main>

      {inspector && (
        <Inspector $open={inspectorOpen && !inspectorCollapsedByViewport}>
          {inspector}
        </Inspector>
      )}

      {/* Mobile drawers */}
      {rail && (
        <>
          <DrawerBackdrop
            $open={railDrawerOpen}
            onClick={() => setRailDrawerOpen(false)}
          />
          <DrawerPanel $open={railDrawerOpen} $side="left" $width={railWidth}>
            {rail}
          </DrawerPanel>
        </>
      )}
      {inspector && (
        <>
          <DrawerBackdrop
            $open={inspectorDrawerOpen}
            onClick={() => setInspectorDrawerOpen(false)}
          />
          <DrawerPanel $open={inspectorDrawerOpen} $side="right" $width={inspectorWidth}>
            {inspector}
          </DrawerPanel>
        </>
      )}
    </Shell>
  );
};

StudioLayout.displayName = 'StudioLayout';
